import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { maskId, validateId, type IdType } from "@despejo/domain";
import { EmptyState, Icon, PageHeader, StatusChip } from "@despejo/ui";
import { useStatuses, useTaxpayers, useInvalidateOrg, workerApi } from "../lib/queries.js";
import type { TaxpayerProfile } from "@despejo/hacienda-client";

const REGIME: Record<string, string> = { GENERAL: "Régimen general", RTS: "Régimen simplificado", UNKNOWN: "Régimen por confirmar" };
const TYPE: Record<string, string> = { PERSONA_FISICA: "Persona física", PERSONA_JURIDICA: "Persona jurídica" };

function NewTaxpayerDialog({ org, onClose }: { org: string; onClose: () => void }) {
  const navigate = useNavigate();
  const invalidate = useInvalidateOrg(org);
  const [idType, setIdType] = useState<IdType>("FISICA");
  const [idNumber, setIdNumber] = useState("");
  const [name, setName] = useState("");
  const [profile, setProfile] = useState<TaxpayerProfile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const v = validateId(idNumber, idType);

  const lookup = async () => {
    if (!v.ok) return;
    setBusy(true);
    setError(null);
    try {
      const r = await workerApi.lookup(v.id);
      if (!r.profile) setError(r.error === "NOT_FOUND" ? "Hacienda no tiene información para esa identificación." : "No se pudo consultar Hacienda. Intente de nuevo en unos minutos.");
      setProfile(r.profile);
      if (r.profile && !name) setName(r.profile.nombre);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const create = async () => {
    if (!v.ok) return;
    setBusy(true);
    setError(null);
    try {
      const r = await workerApi.createTaxpayer({ organization_id: org, id_type: idType, id_number: v.id, display_name: name || undefined });
      await invalidate();
      onClose();
      navigate(`/contribuyentes/${r.taxpayer.id}`);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="new-tp">
      <DialogTitle id="new-tp">Nuevo contribuyente</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField select label="Tipo de identificación" value={idType} onChange={(e) => { setIdType(e.target.value as IdType); setProfile(null); }}>
            <MenuItem value="FISICA">Cédula física</MenuItem>
            <MenuItem value="JURIDICA">Cédula jurídica</MenuItem>
            <MenuItem value="DIMEX">DIMEX</MenuItem>
            <MenuItem value="NITE">NITE</MenuItem>
          </TextField>
          <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
            <TextField
              label="Número"
              value={idNumber}
              onChange={(e) => { setIdNumber(e.target.value); setProfile(null); }}
              error={idNumber !== "" && !v.ok}
              helperText={idNumber !== "" && !v.ok ? v.reason : "Sin guiones ni espacios, o con ellos: los quitamos"}
              inputMode="numeric"
            />
            <Button variant="outlined" onClick={lookup} disabled={!v.ok || busy} sx={{ mt: 1, flexShrink: 0 }}>
              Consultar
            </Button>
          </Stack>
          {profile && (
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1">{profile.nombre}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {profile.regimen.descripcion || "Régimen no indicado"} · {profile.estado ?? "estado no indicado"} · {profile.administracion ?? ""}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <StatusChip tone={profile.moroso ? "error" : "success"} icon={profile.moroso ? "error" : "check"} label={`Moroso: ${profile.moroso ? "SÍ" : "NO"}`} />
                  <StatusChip tone={profile.omiso ? "error" : "success"} icon={profile.omiso ? "error" : "check"} label={`Omiso: ${profile.omiso ? "SÍ" : "NO"}`} />
                </Stack>
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
                  {profile.actividades.length} actividad(es) económica(s)
                </Typography>
              </CardContent>
            </Card>
          )}
          <TextField label="Nombre para mostrar" value={name} onChange={(e) => setName(e.target.value)} helperText="Por ejemplo: “Diego — persona física” o “Sociedad simplificada”" />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={create} disabled={!v.ok || busy || (!profile && !name)}>
          Agregar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function TaxpayersPage({ org }: { org: string }) {
  const [params, setParams] = useSearchParams();
  const taxpayers = useTaxpayers(org);
  const statuses = useStatuses(org);
  const byTp = new Map((statuses.data ?? []).map((s) => [s.taxpayer_id, s]));
  const creating = params.get("nuevo") === "1";

  return (
    <>
      <PageHeader
        title="Contribuyentes"
        subtitle="Cada contribuyente tiene su propio espacio, calendario y documentos."
        actions={
          <Button variant="contained" startIcon={<Icon name="add" />} onClick={() => setParams({ nuevo: "1" })}>
            Agregar
          </Button>
        }
      />
      {taxpayers.data?.length === 0 && <EmptyState icon="badge" title="Sin contribuyentes todavía" body="Agregue una persona física o una sociedad con su número de cédula." />}
      <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" } }}>
        {(taxpayers.data ?? []).map((t) => {
          const s = byTp.get(t.id);
          return (
            <Card key={t.id}>
              <CardActionArea component={Link} to={`/contribuyentes/${t.id}`} sx={{ p: 2 }}>
                <Typography variant="h6">{t.display_name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {TYPE[t.taxpayer_type]} · {REGIME[t.regime]} · {maskId(t.id_number)}
                </Typography>
                <Stack direction="row" spacing={1} useFlexGap sx={{ mt: 1.5, flexWrap: "wrap" }}>
                  {s ? (
                    <>
                      <StatusChip tone={s.moroso ? "error" : "success"} icon={s.moroso ? "error" : "check"} label={`Moroso: ${s.moroso ? "SÍ" : "NO"}`} />
                      <StatusChip tone={s.omiso ? "error" : "success"} icon={s.omiso ? "error" : "check"} label={`Omiso: ${s.omiso ? "SÍ" : "NO"}`} />
                    </>
                  ) : (
                    <StatusChip tone="neutral" icon="hourglass_empty" label="Sin verificar" />
                  )}
                  {t.regime === "UNKNOWN" && <StatusChip tone="warning" icon="help" label="Régimen por confirmar" />}
                </Stack>
              </CardActionArea>
            </Card>
          );
        })}
      </Box>
      {creating && <NewTaxpayerDialog org={org} onClose={() => setParams({})} />}
    </>
  );
}
